import { Box, styled, IconButton } from '@mui/material'
import { colors, spacing, typography, sizing, radius, focusVisible } from '@/styles/designTokens'
import { Card as BaseCard } from '@/components/common/ui/Modal/Modal.styled'

export const StyledModalCard = styled(BaseCard, {
	shouldForwardProp: (prop) => prop !== 'isEdit' && prop !== 'width',
})<{ isEdit: boolean; width: number }>(() => ({
	display: 'flex',
	flexDirection: 'column',
}))

export const StyledContentBox = styled(Box)({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	gap: `${spacing.lg}px`,
	padding: `${spacing.xl}px`,
	alignSelf: 'stretch',
	overflow: 'auto',
	flex: 1,
	minHeight: '280px',
	justifyContent: 'flex-start',
	backgroundColor: colors.surfaceMuted,
})

export const StyledPhoneInputContainer = styled(Box)({
	display: 'flex',
	width: '100%',
	alignItems: 'stretch',

	'& .PhoneInput': {
		display: 'flex',
		width: '100%',
		alignItems: 'stretch',
	},

	// Left box: flag + calling code + dropdown arrow
	'& .PhoneInputCountry': {
		flex: '0 0 auto',
		borderRadius: `${radius.badge}px 0 0 ${radius.badge}px`,
		border: `1px solid ${colors.border}`,
		borderRight: 'none',
		backgroundColor: colors.surfaceWhite,
		padding: `${spacing.lg}px`,
		display: 'flex',
		alignItems: 'center',
		gap: `${spacing.sm}px`,
		margin: 0,
	},

	// Flag icon: 24x24 per Figma
	'& .PhoneInputCountryIcon': {
		width: '24px',
		height: '24px',
		boxShadow: 'none',
		backgroundColor: 'transparent',
	},

	'& .PhoneInputCountryIconImg': {
		width: '100%',
		height: '100%',
	},

	// Calling code text (+1) injected between flag and arrow
	'& .PhoneInputCountry::after': {
		content: 'var(--calling-code)',
		color: colors.textSecondary,
		fontFamily: typography.fontFamily,
		fontSize: `${typography.fontSizeLg}px`,
		fontWeight: typography.fontWeightRegular,
		lineHeight: typography.lineHeightLg,
		letterSpacing: typography.letterSpacingWide,
		whiteSpace: 'nowrap',
		order: 2,
	},

	'& .PhoneInputCountrySelect': {
		order: 0,
	},

	'& .PhoneInputCountrySelectArrow': {
		order: 3,
		marginLeft: 0,
		color: colors.textSecondary,
		opacity: 0.6,
	},

	// Right box: phone number input
	'& input': {
		flex: '1 0 0',
		borderRadius: `0 ${radius.badge}px ${radius.badge}px 0`,
		border: `1px solid ${colors.border}`,
		backgroundColor: colors.surfaceWhite,
		padding: `${spacing.lg}px`,
		color: colors.textSecondary,
		fontFamily: typography.fontFamily,
		fontSize: `${typography.fontSizeLg}px`,
		fontWeight: typography.fontWeightRegular,
		lineHeight: typography.lineHeightLg,
		letterSpacing: typography.letterSpacingWide,
		outline: 'none',
		boxSizing: 'border-box',

		'&::placeholder': {
			color: colors.textSecondary,
		},

		'&:focus-visible': {
			outline: 'none',
			borderColor: colors.focusBorder,
		},
	},
})

export const StyledErrorBox = styled(Box)(({ theme }) => ({
	padding: theme.spacing(1.5, 2),
	borderRadius: theme.shape.borderRadius,
	backgroundColor: colors.errorBg,
	border: `1px solid ${colors.errorBorder}`,
	width: '100%',
}))

export const StyledErrorText = styled(Box)(({ theme }) => ({
	...theme.typography.caption,
	color: theme.palette.error.main,
	margin: 0,
}))

export const StyledButton = styled('button')(({ theme }) => ({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: theme.spacing(1),
	width: '100%',
	border: 'none',
	cursor: 'pointer',
	fontFamily: typography.fontFamily,
	color: colors.textInverse,
	borderRadius: theme.spacing(8),
	padding: theme.spacing(1.5, 2),
	textTransform: 'uppercase',
	letterSpacing: typography.letterSpacingWide,
	fontWeight: typography.fontWeightBold,
	fontSize: `${typography.fontSizeMd}px`,
	lineHeight: typography.lineHeightLg,
	backgroundColor: colors.brand,
	'&:hover': {
		backgroundColor: colors.brandHover,
	},
	'&:focus-visible': {
		...focusVisible,
		outlineColor: colors.brand,
	},
	'&:disabled': {
		backgroundColor: colors.textDisabled,
		color: colors.textDisabledOnDark,
		cursor: 'default',
	},
}))

export const StyledHeaderBox = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	padding: theme.spacing(2, 3),
	borderBottom: `1px solid ${theme.palette.divider}`,
	height: '72px',
}))

export const StyledTitle = styled(Box)({
	color: colors.textPrimary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeXl}px`,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightXl,
	letterSpacing: typography.letterSpacingNormalLg,
	flex: 1,
})

export const StyledCloseButton = styled(IconButton)(({ theme }) => ({
	padding: theme.spacing(1),
	'&:hover': {
		backgroundColor: colors.overlaySubtle,
	},
	'&:focus-visible': {
		...focusVisible,
		outlineColor: colors.brand,
	},
}))

export const StyledFooterBox = styled(Box)({
	display: 'flex',
	padding: `${spacing.lg}px ${spacing.xl}px`,
	justifyContent: 'space-between',
	alignItems: 'center',
	alignSelf: 'stretch',
	borderTop: `1px solid ${colors.divider}`,
})

export const StyledButtonContainer = styled(Box)({
	flex: 1,
	display: 'flex',
	alignItems: 'stretch',
})

export const StyledHelperLinksBox = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(1),
	textAlign: 'center',
	width: '100%',
}))

export const StyledHelperText = styled(Box)(({ theme }) => ({
	...theme.typography.body2,
	color: theme.palette.text.secondary,
	'& a': {
		color: theme.palette.text.primary,
		textDecoration: 'none',
		fontWeight: typography.fontWeightBold,
		borderBottom: `1px solid ${theme.palette.text.primary}`,
		cursor: 'pointer',
		'&:hover': {
			color: colors.brand,
		},
	},
}))

export const StyledVerifyTitle = styled(Box)({
	color: colors.textPrimary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeLg}px`,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightXl,
	letterSpacing: typography.letterSpacingNormalLg,
	textAlign: 'center',
	marginBottom: `${spacing.xxl}px`,
})

export const StyledVerifyText = styled(Box)({
	color: colors.textSecondary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeMd}px`,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	textAlign: 'center',
	marginBottom: `${spacing.sm}px`,
})

export const StyledOtpInputContainer = styled(Box)({
	display: 'flex',
	gap: `${spacing.md}px`,
	justifyContent: 'center',
	marginBottom: `${spacing.xxl}px`,
	width: '100%',
})

export const StyledOtpInput = styled('input')({
	width: `${sizing.addButton + 4}px`,
	height: `${sizing.addButton + 4}px`,
	borderRadius: `${radius.badge}px`,
	border: `1px solid ${colors.border}`,
	backgroundColor: colors.surfaceWhite,
	color: colors.textPrimary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeLg}px`,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightLg,
	textAlign: 'center',
	cursor: 'pointer',
	outline: 'none',
	transition: 'all 0.2s ease',

	'&:focus-visible': {
		...focusVisible,
		outlineColor: colors.brand,
		borderColor: colors.focusBorder,
		boxShadow: `0 0 0 2px ${colors.overlayMedium}`,
	},

	'&:disabled': {
		backgroundColor: colors.overlaySubtle,
		cursor: 'not-allowed',
	},

	'&[aria-invalid="true"]': {
		borderColor: colors.errorBorder,
		backgroundColor: colors.errorBg,
	},
})

export const StyledResultIcon = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'isSuccess',
})<{ isSuccess: boolean }>({
	display: 'flex',
	justifyContent: 'center',
	marginBottom: `${spacing.lg}px`,
})

export const StyledResultTitle = styled(Box)({
	color: colors.textPrimary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeXl}px`,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightXl,
	letterSpacing: typography.letterSpacingNormalLg,
	textAlign: 'center',
	marginBottom: `${spacing.sm}px`,
})

export const StyledResultMessage = styled(Box)({
	color: colors.textSecondary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeMd}px`,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	textAlign: 'center',
})

export const StyledEmailHelperText = styled(Box)({
	color: colors.textPrimary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeMd}px`,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	textAlign: 'left',
	width: '100%',
})

// Email field — stacked label-over-value inside a bordered box (per Figma)
export const StyledEmailFieldBox = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'hasError',
})<{ hasError?: boolean }>(({ hasError }) => ({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	width: '100%',
	boxSizing: 'border-box',
	borderRadius: `${radius.badge}px`,
	border: `1px solid ${hasError ? colors.errorBorder : colors.border}`,
	backgroundColor: colors.surfaceWhite,
	padding: `${spacing.sm}px ${spacing.lg}px`,

	'&:focus-within': {
		borderColor: hasError ? colors.errorBorder : colors.border,
	},
}))

export const StyledEmailLabel = styled('label')({
	color: colors.textSecondary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeMd}px`,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	cursor: 'pointer',
})

export const StyledEmailInput = styled('input')({
	width: '100%',
	border: 'none',
	outline: 'none',
	background: 'transparent',
	padding: 0,
	margin: 0,
	color: colors.textPrimary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeLg}px`,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,

	'&::placeholder': {
		color: colors.textDisabled,
		opacity: 1,
	},

	'&:focus-visible': {
		...focusVisible,
		outlineColor: colors.brand,
	},

	'&:disabled': {
		cursor: 'not-allowed',
	},
})
